import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "@/app/store/hooks";
import {
  inAppNotificationsApi,
  type InAppNotification,
} from "@/features/notifications/api/inAppNotificationsApi";
import { notificationSocketUrl } from "@/features/notifications/lib/socketUrl";
import { useToast } from "@/shared/ui/Toast";

/** Keep the bell current while the socket is up. Polling remains the fallback. */
export function useLiveInAppNotifications() {
  const token = useAppSelector((state) => state.auth.accessToken);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    if (!token) return undefined;
    let socket: WebSocket | null = null;
    let stopped = false;
    let retryMs = 1000;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      if (stopped) return;
      socket = new WebSocket(notificationSocketUrl(token));
      socket.onopen = () => {
        retryMs = 1000;
      };
      socket.onmessage = (event) => {
        let row: InAppNotification;
        try {
          row = JSON.parse(event.data) as InAppNotification;
        } catch {
          return;
        }
        if (!row || typeof row.id !== "number") return;
        dispatch(
          inAppNotificationsApi.util.updateQueryData(
            "listInAppNotifications",
            undefined,
            (draft) => {
              if (draft.results.some((item) => item.id === row.id)) return;
              draft.results.unshift(row);
              draft.results = draft.results.slice(0, 30);
              if (!row.read_at) draft.unread_count += 1;
            },
          ),
        );
        toastRef.current.info(row.subject || "New notification");
      };
      socket.onclose = () => {
        if (stopped) return;
        timer = setTimeout(connect, retryMs);
        retryMs = Math.min(retryMs * 2, 30_000);
      };
    };

    connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      socket?.close();
    };
  }, [token, dispatch]);
}
